import { useSyncExternalStore } from "react";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Stripe } from "@stripe/stripe-js";
import * as Sentry from "@sentry/browser";

// Nach so vielen Fehlversuchen gilt Stripe.js als nicht ladbar und der Checkout
// zeigt eine Fehlermeldung statt endlos "Bitte warten".
const ATTEMPTS_BEFORE_ERROR = 3;
const RETRY_DELAY_MILLISECONDS = 5000;
const MAX_RETRY_DELAY_MILLISECONDS = 30000;

let hasFailed = false;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return hasFailed;
}

function setHasFailed(value: boolean) {
  if (hasFailed === value) {
    return;
  }
  hasFailed = value;
  listeners.forEach((listener) => listener());
}

// true, sobald Stripe.js mehrfach nicht geladen werden konnte (Netzproblem,
// Blocker, Firewall). Der Ladeversuch laeuft danach im Hintergrund weiter.
export function useStripeLoadFailed(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export async function loadStripeWithRetries(apiKey: string): Promise<Stripe | null> {
  for (let attempt = 1; ; attempt++) {
    try {
      const stripe = await loadStripe(apiKey);

      if (stripe) {
        // Spaeter Erfolg nach Fehlversuchen: Fehlermeldung wieder zuruecknehmen
        setHasFailed(false);
        return stripe;
      }

      throw new Error("loadStripe lieferte keine Stripe-Instanz");
    } catch (error) {
      console.error("Stripe.js konnte nicht geladen werden", error);

      if (attempt === ATTEMPTS_BEFORE_ERROR) {
        Sentry.captureMessage(`Stripe.js konnte nach ${attempt} Versuchen nicht geladen werden: ${String(error)}`);
        setHasFailed(true);
      }

      const delay = Math.min(RETRY_DELAY_MILLISECONDS * attempt, MAX_RETRY_DELAY_MILLISECONDS);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}
